import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useIsStaff } from "@/hooks/useIsStaff";

/**
 * The team_member id of the currently logged-in STAFF user in the current salon
 * (null for non-staff or until loaded). Used to lock the calendar + visit form
 * to the staff member's own lane. Mirrors the DB helper current_team_member_id().
 */
export function useStaffTeamMemberId(): string | null {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const isStaff = useIsStaff();

  const { data } = useQuery({
    queryKey: ["staff-team-member-id", currentTenant?.id, user?.id],
    enabled: isStaff && !!currentTenant?.id && !!user?.id,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      // user_id added by migration 20260626120000; not in generated types yet.
      // deno-lint-ignore no-explicit-any
      const { data, error } = await (supabase.from("team_members") as any)
        .select("id")
        .eq("tenant_id", currentTenant!.id)
        .eq("user_id", user!.id)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return (data as { id?: string } | null)?.id ?? null;
    },
  });

  return data ?? null;
}
